import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { handleGet } from "../../../controllers/controller.all";
import { sendNotificationToClass } from "../../../controllers/controller.notification";
import { Send, Bell, Smartphone, Users } from "lucide-react";

export default function SendNotification() {
    const user = useSelector((state) => state.userReducer?.user);
    const token = user?.access_token || "";
    const ecoleId = user?.ecole?._id || user?.ecole;

    const [loading, setLoading] = useState(false);
    const [classes, setClasses] = useState([]);
    
    const [formData, setFormData] = useState({
        classId: "",
        title: "",
        message: "",
        usePush: true,
        useSms: false,
        includeTuteurs: true
    });

    useEffect(() => {
        const fetchClasses = async () => {
            await handleGet(`/classe/ecole/${ecoleId}`, (res) => {
                if (res?.classes) {
                    setClasses(res.classes.filter(c => c.ecole === ecoleId || c.ecole?._id === ecoleId));
                }
            }, token);
        };
        if (token) fetchClasses();
    }, [token, ecoleId]);

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === "checkbox" ? checked : value
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        const success = await sendNotificationToClass(
            formData.classId,
            formData.title,
            formData.message,
            formData.usePush,
            formData.useSms,
            formData.includeTuteurs,
            token,
            ecoleId
        );
        if (success) {
            setFormData(prev => ({ ...prev, title: "", message: "" }));
        }
        setLoading(false);
    };

    return (
        <div className="flex flex-col gap-6 max-w-3xl mx-auto">
            <div>
                <h1 className="text-2xl font-bold text-gray-900 tracking-tight">Notifications & SMS</h1>
                <p className="text-sm text-gray-500 mt-1">Envoyez des messages importants à une classe entière et à leurs tuteurs.</p>
            </div>

            <form onSubmit={handleSubmit} className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                <div className="p-6 space-y-6">
                    {/* Class Selection */}
                    <div className="space-y-2">
                        <label className="text-sm font-medium text-gray-700">Sélectionner une classe *</label>
                        <select 
                            name="classId" 
                            value={formData.classId} 
                            onChange={handleChange} 
                            required 
                            className="w-full border border-gray-300 rounded-lg py-2.5 px-3 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                        >
                            <option value="">-- Choisir une classe --</option>
                            {classes.map(c => <option key={c._id} value={c._id}>{c.libelle}</option>)}
                        </select>
                    </div>

                    <hr className="border-gray-100" />

                    {/* Content */}
                    <div className="space-y-4">
                        <div className="space-y-2">
                            <label className="text-sm font-medium text-gray-700">Titre de la notification *</label>
                            <input 
                                type="text" 
                                name="title" 
                                value={formData.title} 
                                onChange={handleChange} 
                                required 
                                placeholder="Ex: Rappel de réunion" 
                                className="w-full border border-gray-300 rounded-lg py-2.5 px-3 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                            />
                        </div>

                        <div className="space-y-2">
                            <label className="text-sm font-medium text-gray-700">Message *</label>
                            <textarea 
                                name="message" 
                                value={formData.message} 
                                onChange={handleChange} 
                                required 
                                rows="4" 
                                placeholder="Saisissez votre message ici..." 
                                className="w-full border border-gray-300 rounded-lg py-2.5 px-3 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                            ></textarea>
                        </div>
                    </div>

                    <hr className="border-gray-100" />

                    {/* Options */}
                    <div className="space-y-4">
                        <h3 className="text-sm font-medium text-gray-900">Canaux de communication</h3>
                        
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <label className={`flex items-start gap-3 p-4 border rounded-xl cursor-pointer transition ${formData.usePush ? 'border-indigo-500 bg-indigo-50/50' : 'border-gray-200 hover:bg-gray-50'}`}>
                                <div className="flex h-5 items-center">
                                    <input type="checkbox" name="usePush" checked={formData.usePush} onChange={handleChange} className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500" />
                                </div>
                                <div className="flex flex-col">
                                    <span className="text-sm font-medium text-gray-900 flex items-center gap-2"><Bell className="w-4 h-4" /> Notification Push</span>
                                    <span className="text-xs text-gray-500">Apparait sur l'application mobile.</span>
                                </div>
                            </label>

                            <label className={`flex items-start gap-3 p-4 border rounded-xl cursor-pointer transition ${formData.useSms ? 'border-indigo-500 bg-indigo-50/50' : 'border-gray-200 hover:bg-gray-50'}`}>
                                <div className="flex h-5 items-center">
                                    <input type="checkbox" name="useSms" checked={formData.useSms} onChange={handleChange} className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500" />
                                </div>
                                <div className="flex flex-col">
                                    <span className="text-sm font-medium text-gray-900 flex items-center gap-2"><Smartphone className="w-4 h-4" /> SMS</span>
                                    <span className="text-xs text-gray-500">Coûts de messagerie applicables.</span>
                                </div>
                            </label>
                        </div>

                        {formData.useSms && (
                            <label className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg cursor-pointer">
                                <input type="checkbox" name="includeTuteurs" checked={formData.includeTuteurs} onChange={handleChange} className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500" />
                                <span className="text-sm font-medium text-gray-700 flex items-center gap-2"><Users className="w-4 h-4" /> Envoyer également un SMS aux tuteurs / parents</span>
                            </label>
                        )}
                    </div>
                </div>

                <div className="bg-gray-50 px-6 py-4 border-t border-gray-200 flex justify-end">
                    <button 
                        type="submit" 
                        disabled={loading || (!formData.usePush && !formData.useSms)} 
                        className="inline-flex items-center gap-2 px-5 py-2.5 text-sm font-medium text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 transition disabled:opacity-50 disabled:cursor-not-allowed shadow-sm"
                    >
                        <Send className="h-4 w-4" />
                        {loading ? "Envoi en cours..." : "Envoyer le message"}
                    </button>
                </div>
            </form>
        </div>
    );
}
